/**
 * Топ-рядок 4 KPI розділу «Фінансовий результат» на дизайн-токенах:
 * Плановий дохід · Небаланси · Втрачена вигода (заглушка) · Фінансовий результат.
 * Скляні плитки, Urbanist-числа, Lucide-іконки, Δ% до попереднього періоду.
 */
import { useTranslation } from 'react-i18next';
import { Coins, Scale, Hammer, Landmark, ArrowUp, ArrowDown, Minus, Construction } from 'lucide-react';
import type { SummaryBlock } from './overviewTypes';
import { fmtUah, fmtDeltaPct } from './overviewTypes';

// ПДВ (20%) додається ТІЛЬКИ у дві плитки цієї сторінки (рішення користувача):
// «Плановий дохід» і «Фінансовий результат» показуються з ПДВ (×1,2). Δ% не
// змінюється — це відношення, множник скорочується. Блок «Небаланси», портфель
// tabaki і погодинний графік «Плановий дохід» лишаються без ПДВ.
const VAT = 1.2;
const withVat = (v: number | null | undefined) => (v == null ? v : v * VAT);

function Delta({ v }: { v: number | null }) {
  const d = fmtDeltaPct(v);
  const color = d.dir === 'up' ? 'var(--ok)' : d.dir === 'down' ? 'var(--warn)' : 'var(--text-dim)';
  const Ic = d.dir === 'up' ? ArrowUp : d.dir === 'down' ? ArrowDown : Minus;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px]" style={{ color }}>
      <Ic size={12} strokeWidth={2} /> {d.text}
    </span>
  );
}

export default function FinKpiRow({ s, closed }: { s: SummaryBlock | null; closed: boolean }) {
  const { t } = useTranslation();
  const items = [
    { label: `${t('ak_fin_planned_income')} · ${t('ak_fin_with_vat')}`, value: fmtUah(withVat(s?.plannedIncomeUah)), delta: s?.deltaPct.plannedIncome ?? null, Icon: Coins, stub: false },
    { label: closed ? t('ak_fin_imbalances_actual') : t('ak_fin_imbalances'), value: fmtUah(s?.imbalanceCostUah), delta: s?.deltaPct.imbalance ?? null, Icon: Scale, stub: false },
    { label: t('ak_fin_lost_benefit'), value: t('ak_fin_0_uah'), delta: null, Icon: Hammer, stub: true },
    { label: `${t('ak_fin_financial_result')} · ${t('ak_fin_with_vat')}`, value: fmtUah(withVat(s?.financialResultUah)), delta: s?.deltaPct.financialResult ?? null, Icon: Landmark, stub: false },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((k) => (
        <div key={k.label} className="tile p-4 flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-tile-sm flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}>
            <k.Icon size={18} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] mb-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{k.label}</div>
            <div className="font-display text-xl font-normal leading-none truncate" style={{ color: k.stub ? 'var(--text-dim)' : 'var(--text)' }}>
              {k.value}
            </div>
            <div className="mt-1 h-[14px]">
              {k.stub
                ? <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-dim)' }}><Construction size={11} strokeWidth={1.75} /> {t('ak_fin_next_release')}</span>
                : <Delta v={k.delta} />}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
